import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface BillingDocument {
  id: string;
  document_number: string;
  title: string | null;
  document_type: 'order_form' | 'invoice';
  order_id: string;
  shipper_company_id: string;
  transporter_company_id: string | null;
  amount: number;
  vat_rate: number;
  vat_amount: number;
  total_amount: number;
  status: 'draft' | 'sent' | 'paid' | 'cancelled';
  issued_at: string;
  due_date: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  orders?: any;
  shipper_companies?: any;
  transporter_companies?: any;
}

export const useBillingDocuments = () => {
  const queryClient = useQueryClient();

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['billing-documents'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('billing_documents')
        .select(`
          *,
          orders (
            order_number,
            cargo_title,
            pickup_city,
            delivery_city,
            pickup_date,
            delivery_date
          ),
          shipper_companies:companies!shipper_company_id (
            name,
            email,
            phone,
            siret,
            address
          ),
          transporter_companies:companies!transporter_company_id (
            name,
            email,
            phone,
            siret,
            address
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as BillingDocument[];
    },
  });

  const updateDocumentMutation = useMutation({
    mutationFn: async ({ id, status, paid_at, notes, title }: { 
      id: string; 
      status?: string; 
      paid_at?: string | null;
      notes?: string;
      title?: string;
    }) => {
      const updateData: Record<string, any> = {};
      if (status) updateData.status = status;
      if (paid_at !== undefined) updateData.paid_at = paid_at;
      if (notes !== undefined) updateData.notes = notes;
      if (title !== undefined) updateData.title = title.trim();
      
      console.log('📤 Requête de mise à jour vers Supabase:', { id, updateData });

      const { data, error } = await supabase
        .from('billing_documents')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Erreur Supabase:', error);
        throw error;
      }
      
      console.log('✅ Réponse Supabase:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-documents'] });
      toast.success('Document mis à jour');
    },
    onError: (error: any) => {
      toast.error('Erreur lors de la mise à jour du document');
      console.error('Update error:', error);
    },
  });

  return {
    documents,
    isLoading,
    updateDocument: updateDocumentMutation.mutateAsync,
    isUpdating: updateDocumentMutation.isPending,
  };
};
